import { useTranslation } from "react-i18next";
import { Box, CircularProgress, Grid, Typography } from "@mui/material";
import DialogContentWrapper from "../../../common/components/functional/dialogs/DialogContentWrapper";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { UnsavedChangesProvider } from "../../../contexts/UnsavedChangesContext";
import { useEffect, useMemo, useState } from "react";
import { CustomButton } from "../../../common/components/functional/customButton/CustomButton";
import { Add } from "@mui/icons-material";
import { ReactComponent as DocumentIcon } from "../../../common/icons/documentIcon.svg";
import { Clear as DeleteIcon } from "@mui/icons-material";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import {
  fetchCvDownloadUrl,
  fetchCvUploadUrl,
  fetchTalentCv,
  getAttachmentsFromLocalStorage,
  removeCvDocument,
  selectTalentById,
  uploadCvDocument,
} from "../../talents/talentsSlice";
import { isFulfilled } from "@reduxjs/toolkit";
import { TALENT_CV_PREFIX } from "../../../common/helpers/regexes";
import useIsLoading from "../../../hooks/useIsLoading";
import { LocalStorageAttachment, TalentCV } from "../../talents/talentAPI";
import { LOCAL_STORAGE_KEY } from "../constants";
import useBackendPolling from "../../../hooks/useBackendPolling";
import { format, parse } from "date-fns";
import { dateTimeFormat } from "../../../common/helpers/formatDateTime";

export const TalentEditCV = ({
  talentId,
  onClose,
}: {
  talentId: string;
  onClose: () => void;
}) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const talent = useAppSelector((state) => selectTalentById(state, talentId));
  const talentCv = talent?.cv || null;

  const isLoading = useIsLoading(["talents"]);

  const [uploadFile, setUploadFile] = useState<File | null>(null);

  const defaultValues = useMemo(() => {
    return {
      document: talentCv || null,
    };
  }, [talentCv]);

  type Form = {
    document: TalentCV | null;
  };

  const methods = useForm<Form>({
    mode: "onChange",
    defaultValues,
  });

  const { control, formState, getValues } = methods;
  const saveDisabled = isLoading || !formState.isDirty;

  const { isPolling, startPolling, stopPolling, isTimeout, error } =
    useBackendPolling({
      callback: async () => shouldContinuePolling(),
    });

  useEffect(() => {
    if (isTimeout || error) {
      stopPolling();
    }
    return () => stopPolling();
    // eslint-disable-next-line
  }, [isTimeout]);

  const shouldContinuePolling = async () => {
    const attachmentInStorage: LocalStorageAttachment[] = JSON.parse(
      localStorage.getItem(LOCAL_STORAGE_KEY) || "{}",
    ).filter(
      (attachment: any) =>
        attachment.path === "talents" &&
        attachment.pathId[0] === talentId &&
        attachment.pathId[1] === "cv" &&
        attachment.status === "scanning",
    );
    let isCompleted = false;
    // Fetch data from backend. This overwrites state, so we need to
    // check for attachments in scan from local storage.
    dispatch(fetchTalentCv({ talentId }))
      .then((action) => {
        if (isFulfilled(action)) {
          isCompleted =
            attachmentInStorage[0]?.filename === action.payload.cv?.filename;
          dispatch(getAttachmentsFromLocalStorage(talentId as string));
        }
        isCompleted && stopPolling();
      })
      .catch((err) => {
        console.error("Error fetching talent cv", err);
        stopPolling();
      });
  };

  const downloadDocument = () => {
    if (!talentCv) return;
    dispatch(
      fetchCvDownloadUrl({ talentId, pathspec: talentCv.pathspec }),
    ).then((action) => {
      if (isFulfilled(action)) {
        const url = action.payload.url;
        window.open(url, "_blank");
      }
    });
  };

  const uploadDocument = () => {
    if (!uploadFile) return;
    const requestContent = {
      talentId,
      filename: uploadFile.name,
      size: uploadFile.size.toString(),
      content: uploadFile,
    };

    dispatch(
      fetchCvUploadUrl({
        talentId,
        filename: requestContent.filename,
        size: requestContent.size,
      }),
    ).then((action) => {
      if (isFulfilled(action)) {
        const url = action.payload.url;
        dispatch(
          uploadCvDocument({
            ...requestContent,
            url,
          }),
        )
          .then(() => getAttachmentsFromLocalStorage(talentId))
          .finally(() => onClose());
      }
    });
  };

  const removeDocument = () => {
    if (!talentCv) return;
    dispatch(removeCvDocument({ talentId, pathspec: talentCv.pathspec }))
      .then((action) => {
        if (isFulfilled(action)) {
          methods.setValue("document", null);
        }
      })
      .finally(() => onClose());
  };

  const onSave = () => {
    const document = getValues("document");
    if (!document && talentCv) {
      removeDocument();
    } else if (uploadFile) {
      uploadDocument();
    }
  };

  const onCancel = () => {
    onClose();
  };

  useEffect(() => {
    methods.reset(defaultValues);
  }, [defaultValues, methods]);

  useEffect(() => {
    // Check if needs polling when modal is opened
    dispatch(getAttachmentsFromLocalStorage(talentId as string));

    if (!isPolling && defaultValues?.document?.status === "scanning") {
      startPolling();
    }

    return () => stopPolling();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, talentId]);

  return (
    <DialogContentWrapper
      title={t("talentProfile:CV")}
      margin
      onCancel={onCancel}
      onSave={onSave}
      saveDisabled={saveDisabled || isLoading}
      customSaveButtonLabel={t("common:save")}
      customCancelButtonLabel={t("common:close")}
      infoText={t("talentProfile:cvInfo")}
    >
      <FormProvider {...methods}>
        <UnsavedChangesProvider isDirty={methods.formState.isDirty}>
          <Grid container columnSpacing={2} rowSpacing={1}>
            <Grid item textAlign="end" xs={4}>
              <Typography color="textSecondary" mt={"3px"}>
                {t("talentProfile:document")}
              </Typography>
            </Grid>
            <Grid item xs={8}>
              <Controller
                name="document"
                control={control}
                render={({ field: { onChange, value } }) => {
                  const formatFileDate = (filename: string) => {
                    const timestamp = filename.split("_")[0];
                    const date = parse(timestamp, "yyyyMMddHHmmss", new Date());
                    return format(date, dateTimeFormat);
                  };
                  const filename =
                    value && value.filename?.match(TALENT_CV_PREFIX)
                      ? `${value.filename?.slice(52)} (${formatFileDate(value.filename)})`
                      : value?.filename;

                  return (
                    <>
                      <Grid item xs={8}>
                        {value && (
                          <Box
                            display="flex"
                            mb="0.5rem"
                            marginTop="-4px"
                            paddingTop={"6px"}
                          >
                            <Box
                              onClick={
                                value.status !== "scanning"
                                  ? downloadDocument
                                  : undefined
                              }
                              sx={{
                                cursor: "pointer",
                                display: "flex",
                                alignItems: "center",
                                gap: "10px",
                              }}
                            >
                              <DocumentIcon style={{ minWidth: "22px" }} />
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  width: "280px",
                                }}
                              >
                                <Typography>{filename}</Typography>
                                {value.status === "scanning" && (
                                  <CircularProgress
                                    size={16}
                                    sx={{ flexShrink: 0, marginLeft: 1 }}
                                  />
                                )}
                              </Box>
                            </Box>
                            {!uploadFile && value.status !== "scanning" && (
                              <CustomButton
                                sx={{
                                  textTransform: "none",
                                  padding: "0px",
                                }}
                                variant="text"
                                startIcon={
                                  <DeleteIcon sx={{ color: "#00000085" }} />
                                }
                                onClick={() => onChange(null)}
                              >
                                <Typography variant="secondary">
                                  {t("common:delete")}
                                </Typography>
                              </CustomButton>
                            )}
                          </Box>
                        )}
                        <CustomButton
                          sx={{
                            height: "30px",
                            textTransform: "none",
                            borderRadius: "15px",
                            fontWeight: "700",
                          }}
                          variant="outlined"
                          startIcon={<Add />}
                          component="label"
                        >
                          <input
                            type="file"
                            accept=".pdf"
                            hidden
                            onChange={(event) => {
                              const files = event.target.files;
                              if (files && files.length) {
                                const file = files[0];
                                setUploadFile(file);
                                onChange({
                                  filename: file.name,
                                  pathspec: "",
                                  size: file.size,
                                  fileType: file.type,
                                });
                              }
                            }}
                          />
                          {t("common:addFile")}
                        </CustomButton>
                      </Grid>
                    </>
                  );
                }}
              />
            </Grid>
          </Grid>
        </UnsavedChangesProvider>
      </FormProvider>
    </DialogContentWrapper>
  );
};
